import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { SlotInput, scheduleVariantsApi } from '../schedule-variants';

export const scheduleVariantKeys = {
  all: ['admin', 'timetable', 'schedule-variants'] as const,
  detail: (id: string) => ['admin', 'timetable', 'schedule-variants', id] as const,
};

export function useScheduleVariantsQuery() {
  return useQuery({ queryKey: scheduleVariantKeys.all, queryFn: () => scheduleVariantsApi.list() });
}

export function useScheduleVariantQuery(id: string | undefined) {
  return useQuery({
    queryKey: scheduleVariantKeys.detail(id ?? ''),
    queryFn: () => scheduleVariantsApi.get(id as string),
    enabled: !!id,
  });
}

export function useCreateScheduleVariantMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof scheduleVariantsApi.create>[0]) => scheduleVariantsApi.create(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: scheduleVariantKeys.all }),
  });
}

export function useUpdateScheduleVariantMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof scheduleVariantsApi.update>[1] }) =>
      scheduleVariantsApi.update(id, data),
    onSuccess: (_result, variables) => {
      qc.invalidateQueries({ queryKey: scheduleVariantKeys.all });
      qc.invalidateQueries({ queryKey: scheduleVariantKeys.detail(variables.id) });
    },
  });
}

export function useReplaceScheduleVariantSlotsMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, slots }: { id: string; slots: SlotInput[] }) => scheduleVariantsApi.replaceSlots(id, slots),
    onSuccess: (_result, variables) => {
      qc.invalidateQueries({ queryKey: scheduleVariantKeys.all });
      qc.invalidateQueries({ queryKey: scheduleVariantKeys.detail(variables.id) });
    },
  });
}

export function useDeleteScheduleVariantMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => scheduleVariantsApi.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: scheduleVariantKeys.all }),
  });
}
